<template>
  <div>
    <WdsText secondary class="tw-mb-4">
      {{
        $t(
          'Dutch law requires staffing agencies to provide a G-Account (Geblokkeerde Rekening) for tax liability reserves. The payment split percentage depends on your NEN 4400-1 (SNA) certification status.',
        )
      }}
    </WdsText>

    <div class="tw-mb-4 tw-p-4 tw-bg-gray-50 tw-rounded-lg">
      <WdsText weight="semibold" class="tw-mb-2">
        {{ $t('Your G-Account split percentage') }}:
        <span class="tw-text-primary-600">{{ gAccountPercentage }}%</span>
      </WdsText>
      <WdsText secondary size="small">
        <template v-if="isNenCertified">
          {{
            $t(
              'Based on your NEN 4400-1 (SNA) certification, 25% of invoice amounts will be directed to your G-Account.',
            )
          }}
        </template>
        <template v-else>
          {{
            $t(
              'Without NEN 4400-1 (SNA) certification, 55% of invoice amounts will be directed to your G-Account. Consider obtaining certification to reduce this to 25%.',
            )
          }}
        </template>
      </WdsText>
    </div>

    <WdsInput
      v-model="gAccountIban"
      :label="$t('G-Account IBAN')"
      :placeholder="$t('NL00BANK0990000000')"
      :error="ibanError"
      name="g_account_iban"
      required
      @blur="validateIban"
    />
    <WdsText secondary size="small" class="tw-mt-1">
      {{
        $t(
          'Enter your G-Account IBAN. Dutch G-Accounts are recognizable by 099 at positions 9-11 (e.g., NL00BANK0990000000).',
        )
      }}
    </WdsText>

    <div class="tw-mt-6">
      <WdsText weight="bold" class="tw-mb-1">{{ $t('G-Account agreement') }}</WdsText>
      <WdsText secondary size="small" class="tw-mb-2">
        {{ $t('Upload your G-Account agreement document.') }}
      </WdsText>
      <PickerFileFilebank
        v-slot="{ openFilePicker }"
        :default-tags="['g-account-agreement']"
        @files-selected="onFilesSelected"
      >
        <WdsButton type="button" variant="secondary" @click="openFilePicker">
          {{ currentFileId ? $t('Replace document') : $t('Choose file') }}
        </WdsButton>
      </PickerFileFilebank>
      <div v-if="displayFileName" class="tw-flex tw-items-center tw-gap-1 tw-mt-1">
        <WdsText secondary size="small">
          {{ displayFileName }}
        </WdsText>
        <a v-if="currentFileId" :href="`/files/${currentFileId}`" target="_blank" rel="noopener noreferrer">
          <WdsIcon name="download" :size="16" />
        </a>
      </div>
    </div>

    <WdsAlert
      v-if="errorMessage && showError"
      class="tw-mt-4"
      closable
      error
      @close="showError = false"
    >
      {{ $t('An error occurred') }}
      <template #description>
        {{ errorMessage }}
      </template>
    </WdsAlert>

    <WdsAlert v-else-if="showSuccess" class="tw-mt-4" closable success @close="showSuccess = false">
      {{ $t('Your G-Account details have been saved') }}
    </WdsAlert>

    <div class="tw-mt-4">
      <WdsButton :disabled="loading || !isValid" primary @click="onSave">
        <div class="tw-flex tw-items-center">
          <span>{{ $t('Save') }}</span>
          <WdsSpinner v-show="loading" class="tw-ml-2" indeterminate>
            <WdsIcon name="bam" :size="16" />
          </WdsSpinner>
        </div>
      </WdsButton>
    </div>
  </div>
</template>

<script>
import { reactive, ref } from 'vue'
import {
  WdsAlert,
  WdsButton,
  WdsIcon,
  WdsInput,
  WdsSpinner,
  WdsText,
} from '@worksome/wds-components'
import PickerFileFilebank from '@/components/PickerFileFilebank.vue'
import { UpdateComplianceMutation } from '@/components/compliances/mutations/UpdateComplianceMutation'
import { FormValidation } from '@/utils/formValidation'
import { NlSnaStatus } from '@/components/compliances/types/ComplianceName.enum'

const NL_G_ACCOUNT_IBAN_REGEX = /^NL\d{2}[A-Z]{4}099\d{7}$/

export default {
  name: 'NlGAccountSettingsForm',
  components: {
    PickerFileFilebank,
    WdsAlert,
    WdsButton,
    WdsIcon,
    WdsInput,
    WdsSpinner,
    WdsText,
  },
  props: {
    recruiter: {
      type: Object,
      required: true,
    },
    complianceData: {
      type: Object,
      required: true,
    },
    snaStatus: {
      type: String,
      default: null,
    },
  },
  setup(props) {
    const errors = ref([])
    const internalComplianceData = reactive(props.complianceData)
    const loading = ref(false)
    const showError = ref(false)
    const showSuccess = ref(false)
    const ibanTouched = ref(false)
    const pendingFileId = ref(null)
    const pendingFileName = ref(null)
    const savedFileName = ref(null)

    return {
      errors,
      internalComplianceData,
      loading,
      showError,
      showSuccess,
      ibanTouched,
      pendingFileId,
      pendingFileName,
      savedFileName,
    }
  },
  computed: {
    gAccountIban: {
      get() {
        return this.internalComplianceData.gAccountIban || ''
      },
      set(value) {
        this.internalComplianceData.gAccountIban = value.toUpperCase().replace(/\s/g, '')
      },
    },
    isNenCertified() {
      return this.snaStatus === NlSnaStatus.CERTIFIED
    },
    gAccountPercentage() {
      return this.isNenCertified ? 25 : 55
    },
    ibanError() {
      if (!this.ibanTouched) return null
      if (!this.gAccountIban) return this.$t('G-Account IBAN is required')
      if (!NL_G_ACCOUNT_IBAN_REGEX.test(this.gAccountIban)) {
        return this.$t(
          'Invalid G-Account IBAN format. Must be a Dutch IBAN with 099 at positions 9-11 (e.g., NL00BANK0990000000)',
        )
      }
      return null
    },
    isValid() {
      return this.gAccountIban && NL_G_ACCOUNT_IBAN_REGEX.test(this.gAccountIban) && this.currentFileId
    },
    currentFileId() {
      return this.pendingFileId ?? this.internalComplianceData.fileId ?? null
    },
    displayFileName() {
      return this.pendingFileName ?? this.savedFileName ?? this.internalComplianceData.fileName ?? null
    },
    errorMessage() {
      return FormValidation.containsErrorMessage(this.errors)
    },
  },
  methods: {
    validateIban() {
      this.ibanTouched = true
    },
    onFilesSelected(files) {
      const file = files[0]
      if (!file) return

      this.pendingFileId = file.localId
      this.pendingFileName = file.name
    },
    async onSave() {
      this.ibanTouched = true
      if (!this.isValid) return

      this.loading = true

      const data = JSON.stringify({
        g_account_iban: this.gAccountIban,
        file_id: this.pendingFileId ?? this.internalComplianceData.fileId ?? null,
      })

      try {
        await this.$apollo.mutate({
          mutation: UpdateComplianceMutation,
          variables: {
            input: {
              name: 'NL_G_ACCOUNT',
              data,
            },
          },
        })

        this.showSuccess = true
        this.showError = false

        if (this.pendingFileId) {
          this.savedFileName = this.pendingFileName
          this.internalComplianceData.fileId = this.pendingFileId
        }

        this.pendingFileId = null
        this.pendingFileName = null
      } catch ({ graphQLErrors }) {
        this.errors = graphQLErrors
        this.showError = true
        this.showSuccess = false
      } finally {
        this.loading = false
      }
    },
  },
}
</script>
